import { getAuthHeaders } from "@/lib/api";

export type ListFetcher = (opts: {
  headers: Record<string, string>;
  query: { limit: number; offset: number };
}) => Promise<{ data?: unknown; error?: unknown; response?: Response }>;

/**
 * Rows per page — the API's maximum (MAX_LIMIT in
 * backend/src/lib/query-filters.ts). Asking for more is clamped, not refused,
 * so there is no reason to ask for less: at the old 200 the rekvirering page
 * spent five requests on a stretch of rows that now takes one.
 */
export const PAGE_SIZE = 1000;

/**
 * Batches in flight at once.
 *
 * This used to be unbounded — `Promise.all` over every batch opened one
 * paginating chain per 100 ids simultaneously. The rekvirering page
 * (pages/anamnesis.tsx) runs five such fan-outs from a single render, which put
 * ~1300 requests a minute on the wire from one tab: past the edge's per-IP rate
 * limit of 1200/min (infra/roles/app/templates/Caddyfile.j2), which is one
 * budget shared by everyone behind the office NAT and by all of app/onyxia/api
 * at once. The whole office got "Too Many Requests" on both surfaces because one
 * person opened one page. Six keeps a load quick while leaving the budget room
 * for colleagues.
 */
export const MAX_CONCURRENT_BATCHES = 6;

/**
 * Paginate through all results from an API list endpoint.
 *
 * The end of the data is a page shorter than the *honoured* page size, which is
 * not always the requested one: a route may cap `limit` below PAGE_SIZE
 * (`question-responses` caps at 500, surveys/routes.ts) and the API clamps
 * silently rather than erroring. Comparing against `pageSize` would read that
 * first clamped page as the last one and drop every row after it — no error, no
 * empty result, just a short list. So the first response sets the mark and
 * every later page is measured against that instead.
 */
export async function fetchAll<T>(
  fetcher: ListFetcher,
  pageSize = PAGE_SIZE,
): Promise<T[]> {
  const all: T[] = [];
  let offset = 0;
  let honouredPageSize = pageSize;
  let isFirstPage = true;
  while (true) {
    const res = await fetcher({ headers: getAuthHeaders(), query: { limit: pageSize, offset } });
    if (res.error !== undefined || !Array.isArray(res.data)) {
      const status = res.response?.status ?? "unknown";
      const detail = typeof res.error === "object" ? JSON.stringify(res.error) : String(res.error ?? "no data");
      throw new Error(`API request failed (${status}): ${detail}`);
    }
    const data = res.data as T[];
    all.push(...data);
    // Also the guard against looping forever: a zero-length page would
    // otherwise set honouredPageSize to 0 and advance offset by nothing.
    if (data.length === 0) break;
    if (isFirstPage) {
      honouredPageSize = data.length;
      isFirstPage = false;
    } else if (data.length < honouredPageSize) break;
    offset += data.length;
  }
  return all;
}

/**
 * Ids per `in:` filter. A UUID costs 37 characters in the query string, and
 * the API refuses any request whose request line plus headers exceed 16 KB
 * with a 431 — which a cross-origin caller sees as an opaque network failure,
 * not a status code. One `in:` list is therefore only safe while it is
 * bounded, and these lists are not: they are built from every requisition on
 * the rekvirering page, which grows with the dataset. 100 ids keeps a batch
 * under 4 KB, leaving the rest of the budget to the bearer token.
 */
export const ID_BATCH_SIZE = 100;

/** Split ids into batches small enough for one `in:` filter each. */
export function batchIds(ids: string[], batchSize = ID_BATCH_SIZE): string[][] {
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += batchSize) {
    batches.push(ids.slice(i, i + batchSize));
  }
  return batches;
}

/**
 * Run `fn` over `items` with at most `limit` promises outstanding, keeping
 * results in input order. Rejects on the first failure, like `Promise.all`.
 */
async function mapWithLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i]!);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
  return results;
}

/**
 * Fetch a list endpoint filtered by a set of ids, in batches whose URLs stay
 * well inside the request-size limit. Each batch is itself paginated by
 * `fetchAll`, so request and response both scale. At most
 * `MAX_CONCURRENT_BATCHES` run at a time and a failure in any of them rejects,
 * so a partial answer is never mistaken for a complete one.
 */
export async function fetchAllByIds<T>(
  ids: string[],
  fetcher: (idBatch: string[], opts: Parameters<ListFetcher>[0]) => ReturnType<ListFetcher>,
  batchSize = ID_BATCH_SIZE,
): Promise<T[]> {
  const pages = await mapWithLimit(
    batchIds(ids, batchSize),
    MAX_CONCURRENT_BATCHES,
    (batch) => fetchAll<T>((opts) => fetcher(batch, opts)),
  );
  return pages.flat();
}
